import { Fragment, useMemo, useState } from 'react';
import { AlertTriangle, BookOpenCheck, ChevronDown, ChevronRight, Lock, PencilLine } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useClassGradebookQuery } from './useClassGradebookQuery';
import { AssignmentScoreEditor } from './GradeEntryDialog';
import type { ClassOfferingDetail, GradebookFromQuery } from './classDetailTypes';

type Assignment = NonNullable<ClassOfferingDetail['assignments']>[number];
type Score = NonNullable<Assignment['scores']>[number];
type AssignmentTarget = NonNullable<Assignment['targets']>[number];

interface AssignmentColumn {
  assignment: Assignment;
  target: AssignmentTarget;
}

interface Props {
  classOfferingId: string;
  gradebooks: ReadonlyArray<GradebookFromQuery>;
  assignments: ReadonlyArray<Assignment>;
  onChanged: () => void | Promise<unknown>;
}

function formatNumber(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function scoreLabel(score: Score | undefined) {
  if (!score) return { text: '—', title: 'No score entered' };
  if (score.state === 'GRADED') {
    if (score.pointsEarned == null) return { text: '—', title: 'No score entered' };
    return { text: formatNumber(Number(score.pointsEarned)), title: score.comment || 'Graded' };
  }
  if (score.state === 'MISSING') return { text: '0', title: score.comment || 'Missing (counts as zero)' };
  return { text: 'E', title: score.comment || 'Excused (not included)' };
}

function currentGradeLabel(percentage: number | null | undefined, letterGrade?: string | null) {
  if (percentage == null) return '—';

  const percentageLabel = `${formatNumber(percentage)}%`;
  return letterGrade ? `${percentageLabel} · ${letterGrade}` : percentageLabel;
}

export default function GradebookGrid({
  classOfferingId,
  gradebooks,
  assignments,
  onChanged,
}: Props) {
  const orderedGradebooks = useMemo(
    () => [...gradebooks].sort((a, b) => a.name.localeCompare(b.name)),
    [gradebooks]
  );
  const [requestedGradebookId, setRequestedGradebookId] = useState<string | null>(null);
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null);
  const [expandedScoreEditorKey, setExpandedScoreEditorKey] = useState<string | null>(null);
  const selectedGradebook = orderedGradebooks.find((gradebook) => gradebook.id === requestedGradebookId)
    ?? orderedGradebooks[0];
  const pointsGradebookId = selectedGradebook?.gradingMode === 'POINTS'
    ? selectedGradebook.id
    : undefined;
  const { data, loading, error } = useClassGradebookQuery(classOfferingId, pointsGradebookId);
  const queriedGradebook = data?.classOffering?.gradebook;
  const summary = queriedGradebook?.gradebookSummary;
  const finalized = queriedGradebook?.finalized ?? selectedGradebook?.finalized ?? false;
  const finalizationRevision = queriedGradebook?.currentFinalization?.revision
    ?? selectedGradebook?.currentFinalization?.revision;

  const assignmentColumns = useMemo(
    () => assignments.flatMap((assignment): AssignmentColumn[] => {
      if (!selectedGradebook) return [];

      const target = assignment.targets?.find(
        (candidate) => candidate.gradebook.id === selectedGradebook.id
      );
      return target ? [{ assignment, target }] : [];
    }).sort((a, b) => {
      const aDueOn = String(a.assignment.dueOn ?? '');
      const bDueOn = String(b.assignment.dueOn ?? '');
      if (aDueOn && bDueOn && aDueOn !== bDueOn) return aDueOn.localeCompare(bDueOn);
      if (aDueOn && !bDueOn) return -1;
      if (!aDueOn && bDueOn) return 1;
      return (a.assignment.position ?? 0) - (b.assignment.position ?? 0);
    }),
    [assignments, selectedGradebook]
  );

  const scores = useMemo(() => {
    const byAssignmentAndPerson = new Map<string, Score>();
    assignmentColumns.forEach(({ assignment }) => {
      assignment.scores?.forEach((score) => {
        byAssignmentAndPerson.set(`${assignment.id}:${score.person.id}`, score);
      });
    });
    return byAssignmentAndPerson;
  }, [assignmentColumns]);

  const gradebookById = useMemo(
    () => new Map(gradebooks.map((gradebook) => [gradebook.id, gradebook])),
    [gradebooks]
  );
  const editorStudentsById = useMemo(
    () => new Map(
      (summary?.students ?? []).map((student) => [student.person.id, [student.person]] as const)
    ),
    [summary?.students]
  );

  const assignmentIsFinalized = (assignment: Assignment) =>
    (assignment.targets ?? []).some(
      (target) => gradebookById.get(target.gradebook.id)?.finalized
    );

  const header = (
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <BookOpenCheck className="h-5 w-5" />
            Student Grades
          </CardTitle>
          <CardDescription>
            {!selectedGradebook
              ? 'Select an academic gradebook to review.'
              : selectedGradebook.gradingMode === 'POINTS'
                ? `Review current grades in ${selectedGradebook.name}, then expand a student to see assignment history.`
                : `${selectedGradebook.name} uses standards-based assessment.`}
          </CardDescription>
        </div>
        {selectedGradebook && (
          <div className="min-w-56 space-y-1">
            <Label htmlFor="gradebook-grid-selector" className="text-xs text-muted-foreground">
              Academic gradebook
            </Label>
            <Select
              value={selectedGradebook.id}
              onValueChange={(gradebookId) => {
                setRequestedGradebookId(gradebookId);
                setExpandedStudentId(null);
                setExpandedScoreEditorKey(null);
              }}
            >
              <SelectTrigger id="gradebook-grid-selector" aria-label="Academic gradebook">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {orderedGradebooks.map((gradebook) => (
                  <SelectItem key={gradebook.id} value={gradebook.id}>
                    {gradebook.name}
                    {gradebook.gradingMode === 'STANDARDS' ? ' (Standards)' : ''}
                    {gradebook.finalized ? ' (Finalized)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </CardHeader>
  );

  if (!selectedGradebook) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No academic gradebooks are configured for this class.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (selectedGradebook.gradingMode === 'STANDARDS') {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            {selectedGradebook.name} uses standards-based assessment. Use the Standards Assessment section below instead of the numeric grade grid.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading && !summary) {
    return (
      <Card>
        {header}
        <CardContent><p className="text-sm text-muted-foreground">Calculating current grades…</p></CardContent>
      </Card>
    );
  }

  if (error || !summary) {
    return (
      <Card>
        {header}
        <CardContent>
          <p role="alert" className="text-sm text-destructive">
            {error?.message || 'The selected gradebook could not be loaded.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {header}
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            {assignmentColumns.length} targeted assignment{assignmentColumns.length === 1 ? '' : 's'}
          </p>
          <div className="flex flex-wrap gap-2">
            {finalized && (
              <Badge className="gap-1"><Lock className="h-3 w-3" />Finalized r{finalizationRevision}</Badge>
            )}
            <Badge variant="secondary">
              {summary.weighted ? `Weighted categories · ${formatNumber(summary.categoryWeightTotal)}% configured` : 'Total points'}
            </Badge>
          </div>
        </div>
        {finalized && (
          <p className="flex items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            <Lock className="h-4 w-4 shrink-0" />
            This gradebook is read-only. An administrator must reopen it before scores or grading setup can change.
          </p>
        )}
        {summary.uncategorizedAssignmentCount > 0 && (
          <p role="alert" className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {summary.uncategorizedAssignmentCount} uncategorized assignment{summary.uncategorizedAssignmentCount === 1 ? ' is' : 's are'} excluded from weighted grades.
          </p>
        )}

        {summary.students.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Add current members to this gradebook to see grades.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="min-w-56 p-3 text-left font-medium">Student</th>
                  <th className="min-w-28 p-3 text-right font-medium">Current</th>
                  <th className="min-w-24 p-3 text-right font-medium">Points</th>
                  <th className="min-w-64 p-3 text-left font-medium">Progress</th>
                </tr>
              </thead>
              <tbody>
                {summary.students.map((student) => {
                  const droppedAssignmentIds = new Set(
                    student.categories.flatMap((category) => category.droppedAssignmentIds)
                  );
                  const categoryTitle = student.categories
                    .filter((category) => category.percentage != null)
                    .map((category) => `${category.category.name}: ${formatNumber(category.percentage!)}%`)
                    .join(' · ');
                  const fullName = `${student.person.firstName} ${student.person.lastName}`;
                  const expanded = expandedStudentId === student.person.id;
                  const progress = [
                    `${student.includedAssignmentCount} counted`,
                    `${student.ungradedAssignmentCount} ungraded`,
                    `${student.excusedAssignmentCount} excused`,
                    student.droppedAssignmentCount > 0
                      ? `${student.droppedAssignmentCount} dropped`
                      : null,
                  ].filter(Boolean).join(' · ');

                  return (
                    <Fragment key={student.person.id}>
                      <tr className="border-b last:border-0">
                        <th scope="row" className="p-3 text-left font-medium">
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-sm text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={`${expanded ? 'Hide' : 'View'} assignments for ${fullName}`}
                            aria-expanded={expanded}
                            aria-controls={`student-assignment-panel-${student.person.id}`}
                            onClick={() => {
                              setExpandedStudentId(expanded ? null : student.person.id);
                              setExpandedScoreEditorKey(null);
                            }}
                          >
                            {expanded
                              ? <ChevronDown className="h-4 w-4 shrink-0" />
                              : <ChevronRight className="h-4 w-4 shrink-0" />}
                            <span>{student.person.lastName}, {student.person.firstName}</span>
                          </button>
                        </th>
                        <td className="p-3 text-right font-semibold" title={categoryTitle || undefined}>
                          {currentGradeLabel(student.percentage, student.letterGrade)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {formatNumber(student.earnedPoints)} / {formatNumber(student.possiblePoints)}
                        </td>
                        <td className="p-3 text-left text-muted-foreground">{progress}</td>
                      </tr>
                      {expanded && (
                        <tr className="border-b bg-muted/10">
                          <td colSpan={4} className="p-0">
                            <div id={`student-assignment-panel-${student.person.id}`} className="space-y-3 p-4">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <h3 className="font-semibold">Assignments for {fullName}</h3>
                                  <p className="text-xs text-muted-foreground">
                                    {assignmentColumns.length} assignment{assignmentColumns.length === 1 ? '' : 's'} in {selectedGradebook.name}
                                  </p>
                                </div>
                                {finalized && (
                                  <Badge variant="secondary" className="gap-1">
                                    <Lock className="h-3 w-3" />Read only
                                  </Badge>
                                )}
                              </div>

                              {assignmentColumns.length === 0 ? (
                                <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                                  No assignments target this gradebook yet.
                                </p>
                              ) : (
                                <div className="overflow-x-auto rounded-md border bg-background">
                                  <table className="min-w-full text-sm">
                                    <thead>
                                      <tr className="border-b bg-muted/40">
                                        <th className="min-w-44 p-3 text-left font-medium">Assignment</th>
                                        <th className="min-w-24 p-3 text-left font-medium">Due</th>
                                        <th className="min-w-32 p-3 text-left font-medium">Category</th>
                                        <th className="min-w-24 p-3 text-right font-medium">Possible</th>
                                        <th className="min-w-20 p-3 text-right font-medium">Score</th>
                                        <th className="min-w-32 p-3 text-left font-medium">Comment</th>
                                        <th className="min-w-28 p-3 text-right font-medium">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {assignmentColumns.map(({ assignment, target }) => {
                                        const score = scores.get(`${assignment.id}:${student.person.id}`);
                                        const display = scoreLabel(score);
                                        const dropped = droppedAssignmentIds.has(assignment.id);
                                        const editorKey = `${student.person.id}:${assignment.id}`;
                                        const scoreEditorExpanded = expandedScoreEditorKey === editorKey;
                                        const locked = assignmentIsFinalized(assignment);
                                        const targetNames = (assignment.targets ?? []).map(
                                          (assignmentTarget) => assignmentTarget.gradebook.name
                                        );

                                        return (
                                          <Fragment key={assignment.id}>
                                            <tr className="border-b last:border-0">
                                              <td className="p-3">
                                                <p className="font-medium">{assignment.name}</p>
                                                {targetNames.length > 1 && (
                                                  <p className="mt-0.5 text-xs text-muted-foreground">
                                                    Shared across {targetNames.join(', ')}
                                                  </p>
                                                )}
                                              </td>
                                              <td className="whitespace-nowrap p-3 text-muted-foreground">{String(assignment.dueOn ?? '—')}</td>
                                              <td className="p-3">
                                                {target.gradebookCategory?.name ?? (
                                                  selectedGradebook.categories.length === 0
                                                    ? <span className="text-muted-foreground">Total points</span>
                                                    : <span className="text-amber-700">Uncategorized</span>
                                                )}
                                              </td>
                                              <td className="p-3 text-right">
                                                {Number(assignment.pointsPossible) === 0
                                                  ? <Badge variant="secondary">Extra credit</Badge>
                                                  : formatNumber(Number(assignment.pointsPossible))}
                                              </td>
                                              <td
                                                className={`p-3 text-right font-medium ${dropped ? 'text-muted-foreground line-through' : ''}`}
                                                title={dropped ? `${display.title}; dropped from the current grade` : display.title}
                                              >
                                                {display.text}
                                                {dropped && <span className="sr-only"> (dropped)</span>}
                                              </td>
                                              <td className="max-w-56 break-words p-3 text-muted-foreground">{score?.comment || '—'}</td>
                                              <td className="p-3 text-right">
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  disabled={locked}
                                                  aria-label={locked
                                                    ? `${assignment.name} is locked for ${fullName}`
                                                    : `Adjust ${assignment.name} for ${fullName}`}
                                                  aria-expanded={scoreEditorExpanded}
                                                  aria-controls={`student-score-editor-${editorKey}`}
                                                  onClick={() => setExpandedScoreEditorKey(scoreEditorExpanded ? null : editorKey)}
                                                >
                                                  {locked
                                                    ? <Lock className="mr-1 h-3.5 w-3.5" />
                                                    : <PencilLine className="mr-1 h-3.5 w-3.5" />}
                                                  {locked ? 'Locked' : 'Adjust score'}
                                                </Button>
                                              </td>
                                            </tr>
                                            {scoreEditorExpanded && !locked && (
                                              <tr className="border-b bg-muted/10">
                                                <td colSpan={7} className="p-0">
                                                  <div id={`student-score-editor-${editorKey}`} className="space-y-3 p-4">
                                                    <div>
                                                      <h4 className="font-medium">Adjust {assignment.name} for {fullName}</h4>
                                                      <p className="text-xs text-muted-foreground">
                                                        Enter points (including 0), choose Missing or Excused, or leave blank for ungraded.
                                                        {targetNames.length > 1
                                                          ? ` This shared score affects ${targetNames.join(', ')}.`
                                                          : ''}
                                                      </p>
                                                    </div>
                                                    <AssignmentScoreEditor
                                                      assignment={assignment}
                                                      students={editorStudentsById.get(student.person.id) ?? []}
                                                      onComplete={onChanged}
                                                      onCancel={() => setExpandedScoreEditorKey(null)}
                                                      resetKey={editorKey}
                                                      showStudentName={false}
                                                    />
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Expand a student to review assignments. Blank scores are ungraded, 0 counts as zero, E is excluded, and struck-through scores were dropped by the category rule.
        </p>
      </CardContent>
    </Card>
  );
}
